export const modules = [
  "Dashboard",
  "Schedule",
  "Work",
  "People",
  "Clients",
  "Docs",
  "Forms",
  "Finance",
  "Reports",
  "Settings",
];

export const relationshipModels = [
  {
    title: "Flat Team",
    description: "Everyone collaborates without reporting layers. Good for freelancers, agencies, and early startups.",
    examples: ["Founder to all members", "Client to project team", "Peer task ownership"],
  },
  {
    title: "Department Flow",
    description: "Employees belong to departments and managers approve work, leave, and reports.",
    examples: ["Engineering", "Design", "Sales", "Training", "Operations"],
  },
  {
    title: "Matrix Flow",
    description: "People can report to a manager while working under project leads or client owners.",
    examples: ["HR manager", "Project lead", "Client success owner"],
  },
  {
    title: "Custom Relationship",
    description: "Companies define any relationship they need without forcing a fixed org chart.",
    examples: ["Mentor", "Reviewer", "Approver", "Buddy", "Contract owner"],
  },
];

export const roleTemplates = [
  "Founder",
  "Organization Admin",
  "Manager",
  "Employee",
  "Client",
  "Instructor",
  "Intern",
  "BDE/BDA",
  "Freelancer",
];

export const permissionGroups = [
  {
    title: "Company Control",
    permissions: ["Manage workspace", "Manage billing", "Manage branding", "Manage integrations"],
  },
  {
    title: "People Operations",
    permissions: ["Invite members", "Create roles", "Approve leave", "Correct attendance"],
  },
  {
    title: "Work Management",
    permissions: ["Create tasks", "Assign tasks", "Approve work", "Export reports"],
  },
  {
    title: "Client Work",
    permissions: ["Manage leads", "Manage clients", "Client portal access", "Invoices"],
  },
];

export const dashboardMetrics = [
  { label: "Active projects", value: "18", change: "+4 this month" },
  { label: "Team attendance", value: "91%", change: "6 late check-ins" },
  { label: "Open tasks", value: "146", change: "23 due today" },
  { label: "Lead pipeline", value: "42", change: "8 demos scheduled" },
];

export const focusItems = [
  { title: "Approve leave requests", meta: "4 waiting for manager review", status: "People" },
  { title: "Client onboarding checklist", meta: "Acme Digital project needs files", status: "Clients" },
  { title: "Delayed sprint tasks", meta: "7 blocked tasks across 3 projects", status: "Work" },
  { title: "Attendance corrections", meta: "2 missing checkout requests", status: "Reports" },
];

export const companyOnboardingSteps = [
  "Create workspace",
  "Add company profile",
  "Choose relationship model",
  "Invite employees and clients",
  "Configure roles and permissions",
  "Set attendance and leave policies",
  "Create first project or CRM pipeline",
];

export const workPipelines = [
  {
    name: "Task Board",
    stages: ["Backlog", "To Do", "In Progress", "Review", "Done"],
    purpose: "Daily work, internal tasks, client delivery, support tickets, and HR work.",
  },
  {
    name: "Project Flow",
    stages: ["Planning", "Active", "Client Review", "On Hold", "Completed"],
    purpose: "Projects with milestones, files, budgets, client visibility, and delivery tracking.",
  },
  {
    name: "CRM Pipeline",
    stages: ["New Lead", "Contacted", "Demo Scheduled", "Proposal", "Won"],
    purpose: "Lead tracking, client onboarding, follow-ups, BDE/BDA ownership, and conversion reports.",
  },
];

export const peopleRows = [
  { name: "Aarav Mehta", role: "Founder", relation: "Workspace owner", load: "72%", status: "Online" },
  { name: "Nisha Rao", role: "Project Manager", relation: "Reports to founder, leads delivery", load: "84%", status: "In meeting" },
  { name: "Kabir Shah", role: "Developer", relation: "Reports to PM, assigned to client sprint", load: "68%", status: "Working" },
  { name: "Maya Singh", role: "BDE", relation: "Owns leads and commission pipeline", load: "51%", status: "Field work" },
];

export const trackerSignals = [
  "Check-in and checkout time",
  "Active app and task context",
  "Idle time warning",
  "Manual correction request",
  "Daily work summary",
  "Task activity linked to attendance",
];

export const lyricliousImportOptions = [
  "Profile and verified email",
  "Existing Lyriclious role",
  "Training and internship records",
  "Certificates and achievements",
  "Resume profile and skills",
  "Project applications and opportunity history",
];

export const authFlowSteps = [
  "Check current Lyriclious session from the shared backend",
  "If session exists, continue directly into Workspace",
  "If session is missing, redirect to central auth and return to Workspace",
  "After auth, import selected Lyriclious profile data",
  "Map imported user into a company workspace role",
];

export const workItemTypes = [
  {
    type: "Task",
    description: "Planned work with owner, deadline, checklist, comments, and status.",
    examples: ["Design landing page", "Prepare client report", "Create onboarding SOP"],
  },
  {
    type: "Bug",
    description: "Defect with severity, reproduction steps, environment, and fix verification.",
    examples: ["Login redirect fails", "Payment status mismatch", "Mobile layout overflow"],
  },
  {
    type: "Test",
    description: "QA activity linked to a build, feature, task, bug, or release checklist.",
    examples: ["Regression test", "Smoke test", "UAT checklist"],
  },
  {
    type: "Story",
    description: "User-facing requirement with acceptance criteria and delivery notes.",
    examples: ["Client can upload files", "Manager can approve attendance"],
  },
  {
    type: "Epic",
    description: "Large initiative grouping stories, tasks, bugs, tests, docs, and milestones.",
    examples: ["Workspace onboarding", "Client portal", "Attendance tracker"],
  },
  {
    type: "Support Ticket",
    description: "Client or internal request with SLA, priority, assignee, and conversation history.",
    examples: ["Need invoice copy", "Project access issue", "Certificate correction"],
  },
  {
    type: "HR Task",
    description: "People operation item tied to employee, leave, payroll, joining, or exit flow.",
    examples: ["Verify documents", "Approve WFH", "Exit checklist"],
  },
  {
    type: "Sales Follow-up",
    description: "CRM action connected to lead, client, proposal, meeting, and BDE/BDA owner.",
    examples: ["Send proposal", "Demo reminder", "Negotiation note"],
  },
];

export const sprintTypes = [
  {
    name: "Product Sprint",
    cadence: "1-2 weeks",
    purpose: "Feature delivery with stories, tasks, bugs, tests, demos, and release notes.",
  },
  {
    name: "Client Delivery Sprint",
    cadence: "Weekly",
    purpose: "Agency/client work with milestones, approvals, blockers, and client review.",
  },
  {
    name: "Training Sprint",
    cadence: "Batch based",
    purpose: "Instructor-led sessions, assignments, attendance, reviews, and certificates.",
  },
  {
    name: "Hiring Sprint",
    cadence: "Open until filled",
    purpose: "Candidates, interviews, evaluation tasks, offer flow, and onboarding handoff.",
  },
  {
    name: "Sales Sprint",
    cadence: "Weekly or monthly",
    purpose: "Lead targets, demos, proposals, follow-ups, commissions, and won/lost analysis.",
  },
];

export const meetingTypes = [
  "1:1",
  "Daily standup",
  "Sprint planning",
  "Sprint review",
  "Retrospective",
  "Client kickoff",
  "Client review",
  "Interview",
  "Training session",
  "Demo call",
  "Support call",
  "Paid consultation",
];

export const testWorkflows = [
  {
    title: "Smoke Test",
    stages: ["Build ready", "Critical paths", "Pass/Fail", "Release decision"],
  },
  {
    title: "Regression Test",
    stages: ["Scope", "Test cases", "Linked bugs", "Retest", "Sign off"],
  },
  {
    title: "UAT",
    stages: ["Client scenario", "Client feedback", "Fixes", "Approval"],
  },
  {
    title: "Bug Verification",
    stages: ["Fix deployed", "Reproduce", "Verify", "Close/Reopen"],
  },
];

export const workListRows = [
  {
    key: "WS-1042",
    title: "Build company onboarding relationship selector",
    type: "Story",
    owner: "Nisha Rao",
    priority: "High",
    status: "In Progress",
    due: "Today",
    sprint: "Workspace MVP Sprint 01",
  },
  {
    key: "WS-1043",
    title: "Fix attendance summary mismatch for missing checkout",
    type: "Bug",
    owner: "Kabir Shah",
    priority: "Critical",
    status: "Blocked",
    due: "Tomorrow",
    sprint: "Workspace MVP Sprint 01",
  },
  {
    key: "CRM-217",
    title: "Follow up after Acme Digital demo",
    type: "Sales Follow-up",
    owner: "Maya Singh",
    priority: "Medium",
    status: "To Do",
    due: "Jun 24",
    sprint: "June Sales Sprint",
  },
  {
    key: "QA-88",
    title: "Run smoke test for client portal upload flow",
    type: "Test",
    owner: "Aarav Mehta",
    priority: "High",
    status: "Review",
    due: "Jun 25",
    sprint: "Client Delivery Sprint",
  },
  {
    key: "HR-31",
    title: "Create intern onboarding checklist template",
    type: "HR Task",
    owner: "Nisha Rao",
    priority: "Low",
    status: "Done",
    due: "Done",
    sprint: "People Ops Setup",
  },
];

export const kanbanColumns = [
  {
    status: "Backlog",
    items: [
      { key: "WS-1050", title: "Workspace billing plan limits", type: "Task", priority: "Medium" },
      { key: "DOC-12", title: "Project SOP template", type: "Task", priority: "Low" },
    ],
  },
  {
    status: "To Do",
    items: [
      { key: "CRM-217", title: "Follow up after Acme Digital demo", type: "Sales Follow-up", priority: "Medium" },
      { key: "MEET-45", title: "Schedule sprint review", type: "Task", priority: "Low" },
    ],
  },
  {
    status: "In Progress",
    items: [
      { key: "WS-1042", title: "Relationship selector", type: "Story", priority: "High" },
      { key: "API-119", title: "Workspace membership endpoint", type: "Task", priority: "High" },
    ],
  },
  {
    status: "Review",
    items: [
      { key: "QA-88", title: "Smoke test upload flow", type: "Test", priority: "High" },
    ],
  },
  {
    status: "Blocked",
    items: [
      { key: "WS-1043", title: "Attendance summary mismatch", type: "Bug", priority: "Critical" },
    ],
  },
  {
    status: "Done",
    items: [
      { key: "HR-31", title: "Intern onboarding checklist", type: "HR Task", priority: "Low" },
    ],
  },
];

export const sprintCeremonies = [
  { name: "Planning", output: "Sprint goal, scope, owners, capacity, and risk notes" },
  { name: "Daily Standup", output: "Progress, blockers, attendance context, and focus items" },
  { name: "Review", output: "Completed work, client feedback, demos, and approvals" },
  { name: "Retrospective", output: "What worked, what slowed down, improvement actions" },
  { name: "Release Check", output: "Tests, bugs, docs, handoff, and deployment readiness" },
];

export const projectCards = [
  {
    name: "Workspace MVP",
    client: "Internal",
    status: "Active",
    health: "82%",
    budget: "Product",
    milestone: "Work management shell",
  },
  {
    name: "Acme Digital Portal",
    client: "Acme Digital",
    status: "Client Review",
    health: "74%",
    budget: "₹1.8L",
    milestone: "File upload approval",
  },
  {
    name: "Training Ops Automation",
    client: "Lyriclious Academy",
    status: "Planning",
    health: "91%",
    budget: "Internal",
    milestone: "Attendance + certificates",
  },
];

export const attendanceRows = [
  { employee: "Nisha Rao", mode: "Hybrid", checkIn: "09:42", hours: "6h 25m", status: "Present", signal: "2 focus sessions" },
  { employee: "Kabir Shah", mode: "Remote", checkIn: "10:08", hours: "5h 52m", status: "Late", signal: "Missing break note" },
  { employee: "Maya Singh", mode: "Field", checkIn: "09:18", hours: "7h 05m", status: "Present", signal: "3 client visits" },
  { employee: "Aarav Mehta", mode: "Office", checkIn: "09:31", hours: "6h 44m", status: "Present", signal: "Review block" },
];

export const leaveRequests = [
  { employee: "Riya Kapoor", type: "Sick Leave", dates: "Jun 24", impact: "Low", status: "Pending" },
  { employee: "Kabir Shah", type: "Work From Home", dates: "Jun 25-26", impact: "Medium", status: "Manager Review" },
  { employee: "Aman Verma", type: "Exam Leave", dates: "Jun 28", impact: "Low", status: "Approved" },
];

export const clientAccounts = [
  { name: "Acme Digital", stage: "Client", owner: "Nisha Rao", next: "Milestone review", value: "₹1.8L" },
  { name: "Northstar Learning", stage: "Proposal Sent", owner: "Maya Singh", next: "Follow-up call", value: "₹95K" },
  { name: "Bytecraft Studio", stage: "Demo Scheduled", owner: "Aarav Mehta", next: "Workspace demo", value: "₹2.4L" },
  { name: "Campus Hive", stage: "New Lead", owner: "Maya Singh", next: "Qualify lead", value: "₹60K" },
];

export const docSpaces = [
  { name: "SOPs", docs: 18, linked: "Operations, HR, delivery", status: "Internal" },
  { name: "Project Docs", docs: 27, linked: "Projects, tasks, milestones", status: "Mixed" },
  { name: "Client Requirements", docs: 12, linked: "Clients, tickets, approvals", status: "Client-visible" },
  { name: "Training Material", docs: 34, linked: "Sessions, students, assignments", status: "Internal" },
];

export const formTemplates = [
  { name: "Client onboarding", convertsTo: "Client + project + checklist", fields: 14 },
  { name: "Employee onboarding", convertsTo: "Employee profile + HR tasks", fields: 22 },
  { name: "Lead capture", convertsTo: "Lead + BDE follow-up", fields: 8 },
  { name: "Daily standup", convertsTo: "Progress note + blockers", fields: 5 },
];

export const financeRows = [
  { label: "Client invoices", value: "₹4.6L", note: "₹1.2L overdue" },
  { label: "Wallet rewards", value: "18,420 credits", note: "6 approvals pending" },
  { label: "BDE commissions", value: "₹38K", note: "3 payout requests" },
  { label: "Project budget used", value: "64%", note: "2 projects need review" },
];

export const reportCards = [
  { name: "Task completion", metric: "78%", detail: "14 delayed items" },
  { name: "Project progress", metric: "68%", detail: "3 client-review projects" },
  { name: "Attendance", metric: "91%", detail: "6 late check-ins" },
  { name: "Lead conversion", metric: "24%", detail: "8 demos scheduled" },
  { name: "Revenue", metric: "₹7.4L", detail: "₹1.2L pending" },
  { name: "Workload", metric: "72%", detail: "2 overloaded members" },
];

export const automationRules = [
  "Booking completed creates lead, meeting note, and follow-up task",
  "Won lead creates client, project, onboarding form, and kickoff checklist",
  "Blocked task notifies manager and adds risk to project health",
  "Late check-in alerts manager and marks attendance for review",
  "Task completed can trigger wallet credits or milestone progress",
  "Leave approval updates team calendar and capacity report",
];

export const settingsGroups = [
  { title: "Workspace", items: ["Company profile", "Logo", "Color theme", "Locations", "Working days"] },
  { title: "Access", items: ["Roles", "Permissions", "Client portal", "Audit logs"] },
  { title: "Operations", items: ["Attendance policy", "Leave policy", "Task statuses", "SLA rules"] },
  { title: "Integrations", items: ["Lyriclious SSO", "Google Calendar", "Zoom/Meet", "Tracker apps"] },
];

export const brandingOptions = [
  { name: "Lyriclious Purple", color: "#7c3aed", usage: "Default Lyriclious Workspace theme" },
  { name: "Founder Blue", color: "#2563eb", usage: "SaaS, product, and client-service teams" },
  { name: "Growth Green", color: "#16a34a", usage: "Sales, HR, and field teams" },
  { name: "Studio Slate", color: "#475569", usage: "Agencies, design teams, and consultants" },
  { name: "Custom Brand", color: "#b45309", usage: "Use organization-selected primary color" },
];
